
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colors, fonts, glyphs, frames, useMediaQuery } from "../../../Styles";
import { NewCreatableSelect, NewInputList, NewInput } from "../../../Components";
import { ListStyle_Types, ButtonTypes, NewFieldStyles, NewInputListHandles, ListData_ValueTypes, TableStyles_Types, ValueSteps } from "../../../Definitions";

// Data
import { Graph_QL, Log_Item, useGetAutopig, useUser } from "../../../Services/Agrisys_GqlDataCaller"
import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../Components/Tables/reactiveTableRenderer";
import {
	AllRfidItem, AllRfidResponse, DailyScalesByRFIDItem, DailyScalesByRFIDResponse, GET_ALLRFIDS, GET_DAILYSCALESBYRFID,
	GET_LOGS, GET_CHANGEFIRMWARE, GetAllRfidArgs, GetDailyScalesByRFIDArgs, GetLogsArgs, LogItem, LogsResponse,
	ChangeFirmwareItem, GetChangeFirmwareArgs, ChangeFirmwareResponse, GET_CHANGEVMSOFTWARE, ChangeVmSoftwareItem,
	ChangeVmSoftwareResponse, GetChangeVmSoftwareArgs, AvailableBinsResponse, GET_AVAILABLE_BINS, AvailableVmsResponse,
	GET_AVAILABLE_VMS, GetGlobalSettingsResponse, GET_GETGLOBALSETTINGS, Plc_Dataset, ChangeGlobalSettingsItem,
	ChangeGlobalSettingsResponse, GET_CHANGEGLOBALSETTINGS, GetChangeGlobalSettingsArgs, SetWeightLimitsItem,
	SetWeightLimitsResponse, GET_SETWEIGHTLIMITS, WeightLimits, GetSetWeightLimitsArgs, ResetScaleItem, ResetScaleResponse,
	GET_RESETSCALE, GET_LIVE_STATE, LiveStateItem, StartConfigsResponse, GetStartConfigs, GetStartConfigsArgs, GET_TARASCALE,
	TaraScaleItem, TaraScaleResponse, GET_CALIBRATESCALE, CalibrateScaleResponse, GetCalibrateScaleArgs, ChangeMdiItem,
	GetChangeMdiArgs, ChangeMdiResponse, GET_CHANGEMDI, ChangeGateItem, GetChangeGateArgs, GET_CHANGEGATE,
	ChangeGateResponse, ChangeDomainItem, GetChangeDomainArgs, ChangeDomainResponse, GET_CHANGEDOMAIN, ChangeIpItem,
	GetChangeIpArgs, ChangeIpResponse, GET_CHANGEIP, GetDailyTempArgs, DailyTempItem, DailyTempResponse, GET_DAILYTEMPERATURES,
	DailyWaterResponse, DailyWaterItem, GetDailyWaterArgs, GET_DAILYWATERS, DailyScaleItem, GetDailyScaleArgs,
	DailyScaleResponse, GET_DAILYSCALES, GET_TABLE_FROM_DEVICE, TableFromDeviceResponse
} from "../../../Services/Agrisys_GqlDataCaller/DeviceCalls/device_QlCalls";
import { useColumnConstructor } from "../../../Components/Tables/_Tables/useColumnConstructor";
import NewButton, { NewButtonList } from "../../../Components/BaseComponents/newButton";
import { LoadingSpinner } from "../../../Services/wrapper";

export default function GraphQL() {
	return <GraphQl />;
};
/* eslint-disable */
enum LogBtns {
	GetLog = "Get Log",
	LiveScales = "Live Scale",
	InternalScales = "Internal Scale",
	DailyScales = "Daily Scales",
	Temps = "Daily Temps",
	Waters = "Daily Water",
	AllRfids = "All Rfids",
	DailyScalesByRFID = " RFID Scales",
}

enum SoftwareBtns {
	Firmware = "Firmware",
	VmSoftware = "Vm Software",
	GlobalSettings = "Global Settings",
	WeightLimits = "Weight Limits"
}

enum ConfigurationBtns {
	ChangeGate = "Change Gate",
	ChangeMdi = "Change MDI",
	ChangeDomain = "Change Domain",
	ChangeIp = "Change IP",
}
/* eslint-enable */

// #region | ViewBlocks Logs and Data
function DefaultViewBlock() {
	return (
		<div style={{ textAlign: "center", paddingTop: "20px" }}>
			Please select a menu
		</div>
	)
}
function formatLogTimestamp(ts: string | number | Date) {
	if (ts instanceof Date) return ts.toLocaleString();
	if (!isNaN(Number(ts))) return new Date(Number(ts) * 1000).toLocaleString();
	return String(ts);
}
// #endregion

// #region | Log
interface LogViewBlock_Props {
	LogData?: [string, Log_Item],
	ConnectionArgs: { domain: string, gate: number, device: number }
}

interface LogProps {
	args: GetLogsArgs
}
function useLegacyGetLog({ args }: LogProps) {
	const client = useApolloClient();
	const [data, setData] = useState<LogItem[] | undefined>();
	const [loading, setLoading] = useState<boolean>();
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);


			const { data: fetchedData } = await client.query<LogsResponse>({
				query: GET_LOGS,
				variables: args,
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});
			if (!fetchedData.getLog || fetchedData.getLog.length === 0) {
				setError("No data found");
				setData(undefined);
				return;
			}

			setData(fetchedData.getLog);
		} catch (err: any) {
			const message =
				err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return { data, loading, error, fetchData };
}

function LogViewBlock({ LogData, ConnectionArgs }: LogViewBlock_Props) {
	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		fetch: true
	}), [ConnectionArgs]);

	const { data, loading, fetchData } = useLegacyGetLog({ args });
	const [displayLimit, setDisplayLimit] = useState(500);

	const tableData = useMemo(() => {
		return (
			data?.slice(0, displayLimit).map((logItem) => ({
				id: logItem.id,
				gatewayindex: logItem.gatewayindex,
				mapdeviceindex: logItem.mapdeviceindex,
				timestamp: formatLogTimestamp(logItem.timestamp),
				message: logItem.message,
				fetch: ""
			})) || []
		);
	}, [data, displayLimit]);

	const fetchButtonColumn = {
		headerValue: (
			<NewButton
				onClick={() => fetchData()}
				content={loading ? "Loading..." : "Fetch"}
				disabled={loading}
				overrides={{
					baseStyles: { height: "30px", width: "fit-content" }
				}}
			/>
		),
		accessor: "fetch",
		type: undefined as any,
		cell: () => <></>,
		width: "5em"
	};

	const accessorData = useMemo(() => ([
		fetchButtonColumn,
		{ headerValue: "Time", accessor: "timestamp", type: ListData_ValueTypes.String, width: "12em" },
		{ headerValue: "Note", accessor: "message", type: ListData_ValueTypes.String },
		{ headerValue: "Gateway", accessor: "gatewayindex", type: ListData_ValueTypes.Number, width: "7em" },
		{ headerValue: "Mapdevice", accessor: "mapdeviceindex", type: ListData_ValueTypes.Number, width: "7em" },
	]), [loading]);

	const columns = useColumnConstructor({
		accessorData: [...accessorData],
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	const reactiveTable = <>{ReactiveTable({ table, tableType: TableStyles_Types.primaryTable, emptyText: "", renderFooter: false })}</>

	return (
		<div>
			<frames.Graph_QL_inner_content_scroll>
				{reactiveTable}
			</frames.Graph_QL_inner_content_scroll>
		</div>
	);
}
// #endregion

// #region | Live Scale
interface LiveViewBlock_Props {
	LiveScales?: LiveStateItem[];
	ConnectionArgs: { domain: string; gate: number; device: number };
}

function LiveViewBlock({ LiveScales, ConnectionArgs }: LiveViewBlock_Props) {
	const client = useApolloClient();
	const [value, setValue] = useState<number>(0);
	const [localData, setLocalData] = useState<TaraScaleItem[] | undefined>();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const { data, startPolling, stopPolling, refetch } = useQuery(GET_LIVE_STATE, {
		variables: { domain: ConnectionArgs.domain, gate: ConnectionArgs.gate, device: ConnectionArgs.device },
		pollInterval: 0,
		context: { clientName: "legacy" },
	});

	useEffect(() => {
		startPolling(1000);
		return () => stopPolling();
	}, [startPolling, stopPolling]);

	const taraScale = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await client.mutate<TaraScaleResponse>({
				mutation: GET_TARASCALE,
				variables: {
					domain: ConnectionArgs.domain,
					gatewayindex: ConnectionArgs.gate,
					mapdeviceindex: ConnectionArgs.device,
				},
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",
			});
			setLocalData(response.data?.taraScale)
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
		} finally {
			setLoading(false);
		}
	}, [client, ConnectionArgs]);
	const args = useMemo(
		() => ({
			domain: ConnectionArgs.domain,
			gate: ConnectionArgs.gate,
			device: ConnectionArgs.device,
			value
		}),
		[ConnectionArgs, value]
	);

	const calibrateScale = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			await client.mutate<CalibrateScaleResponse>({
				mutation: GET_CALIBRATESCALE,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					value: args.value,
				},
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",

				update: (cache) => {
					try {
						const existing = cache.readQuery<CalibrateScaleResponse, GetCalibrateScaleArgs>({
							query: GET_CALIBRATESCALE,
							variables: {
								domain: args.domain,
								gate: args.gate,
								device: args.device,
								value: value
							},
						});
					} catch (err) {
						console.warn("Cache read failed:", err);
					}
				},
			});
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
		} finally {
			setLoading(false);
		}
	}, [client, ConnectionArgs, value]);

	if (loading) return <div>Loading live scales...</div>;
	if (error) return <div>Error fetching live scales: {error.message}</div>;

	const scalesToRender = LiveScales ?? (data ? [data.getOnlineState] : []);

	return (
		<div>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={() => refetch()}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
					}}
				/>
				<frames.Graph_QL_header>
					<h2>Live Scales</h2>
				</frames.Graph_QL_header>
				<p>Data refreshes every second</p>
				<frames.GraphQLTables>
					<table style={{ maxWidth: "600px" }}>
						<tbody>
							{scalesToRender.length > 0 ? (
								scalesToRender.map((scale, index) => (
									<React.Fragment key={index}>
										<tr>
											<frames.GraphQLTableLong><th>Livescale:</th></frames.GraphQLTableLong>
											<td>{scale.value1 != null ? `${scale.value1}g` : "No data available"}</td>
										</tr>
										<tr>
											<th>AVG:</th>
											<td>{scale.value2 != null ? `${scale.value2 * 10}g` : "No data available"}</td>
										</tr>
										<tr>
											<th>Registered Weights:</th>
											<td>{scale.value3 != null ? `${scale.value3}` : "No data available"}</td>
										</tr>
										<tr>
											<th>Yesterday AVG:</th>
											<td>{scale.value4 != null ? `${scale.value4}g` : "No data available"}</td>
										</tr>
									</React.Fragment>
								))
							) : (
								<tr>
									<td colSpan={2} style={{ textAlign: "center", padding: "1rem" }}>
										No data available
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</frames.GraphQLTables>

				<frames.Graph_QL_header>
					<NewButton
						onClick={taraScale}
						disabled={loading}
						content={loading ? "Loading..." : "Reset tera"}
						overrides={{
							baseStyles: { height: "30px", width: "fit-content", marginTop: "1rem" }
						}}
					/>
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<NewInput
							placeholder={"Calibration value (kg)"}
							onChange={
								(newValue: string) => {
									const num = parseFloat(newValue);
									setValue(isNaN(num) ? 0 : Math.round(num * 1000)); // convert kg to g
								}
							}
							valueOverrides={{
								defaultValue: (value / 1000), // show as kg
								valueType: "number",
								valueStep: ValueSteps.floats
							}}
							isRequired={true}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
						/>kg
						<NewButton
							onClick={calibrateScale}
							disabled={loading}
							content={loading ? "Loading..." : "Calibrate"}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content" }
							}}
						/>
					</div>
				</frames.Graph_QL_header>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Internal Scale
interface InternalViewBlock_Props {
	LiveScales?: (Omit<any, "con"> & Pick<Graph_QL.ConnectionObj, "domain">)[];
	ConnectionArgs: { domain: string; gate: number; device: number };
}
function InternalViewBlock({ LiveScales, ConnectionArgs }: InternalViewBlock_Props) {
	const client = useApolloClient();
	const [value, setValue] = useState<number>(0);
	const [data, setData] = useState<ResetScaleItem[] | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const args = useMemo(
		() => ({
			domain: ConnectionArgs.domain,
			gate: ConnectionArgs.gate,
			device: ConnectionArgs.device,
			value
		}),
		[ConnectionArgs, value]
	);

	const { data: liveStateData, refetch } = useQuery(GET_LIVE_STATE, {
		variables: {
			domain: ConnectionArgs.domain,
			gate: ConnectionArgs.gate,
			device: ConnectionArgs.device,
		},
		context: { clientName: "legacy" },
		fetchPolicy: "network-only",
	});

	const resetScaleData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			await client.mutate<ResetScaleResponse>({
				mutation: GET_RESETSCALE,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					value: args.value,
				},
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",
			});

			await refetch();
		} catch (err: any) {
			const message =
				err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}

	}, [client, args, refetch]);

	return (
		<div>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={refetch}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
					}}
				/>
				<frames.Graph_QL_header><h2>Internal Scales</h2></frames.Graph_QL_header>

				<frames.GraphQLTables>
					<table style={{ maxWidth: "600px" }}>
						<tbody>
							<tr>
								<frames.GraphQLTableLong><th>Livescale:</th></frames.GraphQLTableLong>
								<td>
									{liveStateData?.getOnlineState?.value1 != null
										? `${liveStateData.getOnlineState.value1}g`
										: "No data available"}
								</td>
							</tr>
							<tr>
								<th>TodayAvg:</th>
								<td>
									{liveStateData?.getOnlineState?.value2 != null
										? `${liveStateData.getOnlineState.value2}g`
										: "No data available"}
								</td>
							</tr>
							<tr>
								<th>RegistredWeights:</th>
								<td>
									{liveStateData?.getOnlineState?.value3 != null
										? `${liveStateData.getOnlineState.value3}g`
										: "No data available"}
								</td>
							</tr>
							<tr>
								<th>YesterdayAvg:</th>
								<td>
									{liveStateData?.getOnlineState?.value4 != null
										? `${liveStateData.getOnlineState.value4}g`
										: "No data available"}
								</td>
							</tr>
							<tr>
								<th>Min:</th>
								<td>
									{liveStateData?.getOnlineState?.value5 != null
										? `${liveStateData.getOnlineState.value5}g`
										: "No data available"}
								</td>
							</tr>
							<tr>
								<th>Max:</th>
								<td>
									{liveStateData?.getOnlineState?.value6 != null
										? `${liveStateData.getOnlineState.value6}g`
										: "No data available"}
								</td>
							</tr>
						</tbody>
					</table>
				</frames.GraphQLTables>
				<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
					<NewInputList
						placeholders={["Scale reset value (kg)"]}
						onChanges={[
							(newValue: string) => {
								if (!newValue) return setValue(0);
								const sanitized = newValue.replace(/[^\d.,]/g, "");
								const normalized = sanitized.replace(",", ".");
								const num = parseFloat(normalized);
								if (isNaN(num) || num < 0) {
									setValue(0);
								} else {
									const capped = Math.min(num, 1000);
									setValue(Math.round(capped * 1000));
								}
							},
						]}
						valueOverrides={{
							defaultValues: [(value / 1000).toString().replace(",", ".")],
							valueTypes: ["text"],
						}}
						isRequired={[true]}
						fieldOverrides={{
							fieldStyle: NewFieldStyles.greyTertiary,
						}}
						listOverrides={{
							listStyle: ListStyle_Types.vertical,
						}}
					/>kg
					<NewButton
						onClick={resetScaleData}
						disabled={loading || value === 0}
						content={loading ? "Loading..." : "Reset scale"}
						overrides={{
							baseStyles: { height: "30px", width: "fit-content" }
						}}
					/>

				</div>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Daily Scale
interface DailyViewBlock_Props {
	DailyScaleData?: [string, DailyScaleItem];
	ConnectionArgs: { domain: string; gate: number; device: number };
}
interface DailyProps {
	args: GetDailyScaleArgs;
}

function useLegacyDailyview({ args }: DailyProps) {
	const client = useApolloClient();
	const [data, setData] = useState<DailyScaleItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.query<DailyScaleResponse>({
				query: GET_DAILYSCALES,
				variables: args,
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only",
			});

			const scales = fetchedData?.getDailyScales ?? [];
			setData(scales);
			if (scales.length === 0) {
				setError("No data available");
			}
		} catch (err: any) {
			console.error("Scale fetch error:", err);
			setError(err?.message || err?.networkError?.message || "Network error");
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return { data, loading, error, fetchData };
}

function DailyViewBlock({ ConnectionArgs }: DailyViewBlock_Props) {
	const args = useMemo<GetDailyScaleArgs>(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		humantime: true,
		lastDays: 5,
	}), [ConnectionArgs]);

	const { data: Scales, loading, error, fetchData } = useLegacyDailyview({ args });

	return (
		<frames.Graph_QL_sections>
			<NewButton
				onClick={fetchData}
				content={loading ? "Loading..." : "Fetch"}
				disabled={loading}
				overrides={{
					baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
				}}
			/>
			<frames.Graph_QL_header>
				<h2>Daily Scales</h2>
			</frames.Graph_QL_header>

			<frames.GraphQLTables>
				<table>
					<thead>
						<tr>
							<th>Date:</th>
							<th>Avg:</th>
							<th>Count:</th>
							<th>Min:</th>
							<th>Max:</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={5} style={{ textAlign: "center", padding: "1rem" }}>
									Loading...
								</td>
							</tr>
						) : error ? (
							<tr>
								<td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "#880000" }}>
									{error}
								</td>
							</tr>
						) : Scales && Scales.length > 0 ? (
							Scales.map((scale, index) => (
								<tr key={index}>
									<td>{formatLogTimestamp(scale.timestamp)}</td>
									<td>{scale.value1}g</td>
									<td>{scale.value2}</td>
									<td>{scale.value3}g</td>
									<td>{scale.value4}g</td>
								</tr>
							))
						) : (
							<tr>
								<td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "#880000" }}>
									No data available
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</frames.GraphQLTables>
		</frames.Graph_QL_sections>
	);
}
// #endregion

// #region | Daily Water
interface WaterViewBlock_Props {
	DailyWaterData?: [string, DailyWaterItem];
	ConnectionArgs: { domain: string, gate: number, device: number };
}

interface WaterProps {
	args: GetDailyWaterArgs;
}
function useLegacyGetWaters({ args }: WaterProps) {
	const client = useApolloClient();
	const [data, setData] = useState<DailyWaterItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.query<DailyWaterResponse>({
				query: GET_DAILYWATERS,
				variables: args,
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only",
			});

			const waters = fetchedData?.getDailyWater ?? [];
			setData(waters);
			if (waters.length === 0) {
				setError("No data available");
			}
		} catch (err: any) {
			console.error("Water fetch error:", err);
			setError(err?.message || err?.networkError?.message || "Network error");
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return { data, loading, error, fetchData };
}

function WaterViewBlock({ ConnectionArgs }: WaterViewBlock_Props) {
	const args = useMemo<GetDailyWaterArgs>(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		lastDays: 10,
	}), [ConnectionArgs]);

	const { data: Waters, loading, error, fetchData } = useLegacyGetWaters({ args });

	return (
		<>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={fetchData}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
					}}
				/>
				<frames.Graph_QL_header>
					<h2>Daily Waters</h2>
				</frames.Graph_QL_header>

				<frames.GraphQLTables>
					<table>
						<tbody>
							<tr>
								<th>Date:</th>
								<th>Avg:</th>
								<th>Count:</th>
								<th>Min:</th>
								<th>Max:</th>
							</tr>
							{loading ? (
								<tr>
									<td colSpan={5} style={{ textAlign: "center", padding: "1rem" }}>
										Loading...
									</td>
								</tr>
							) : error ? (
								<tr>
									<td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "#880000" }}>
										{error}
									</td>
								</tr>
							) :
								Waters && Waters.length > 0 ? (
									Waters.map((waters, index) => {
										return (
											<tr key={index}>
												<td>{formatLogTimestamp(waters.timestamp)}</td>
												<td>{waters.value1}</td>
												<td>{waters.value2}</td>
												<td>{waters.value3}</td>
												<td>{waters.value4}</td>
											</tr>
										);
									})
								) : (
									<tr>
										<td colSpan={10}>no data available</td>
									</tr>
								)}
						</tbody>
					</table>
				</frames.GraphQLTables>
			</frames.Graph_QL_sections>
		</>
	);
}
// #endregion

// #region | Daily Temps
interface TempViewBlock_Props {
	DailyTempData?: [string, DailyTempItem];
	ConnectionArgs: { domain: string, gate: number, device: number };
}

interface DailyTempProps {
	args: GetDailyTempArgs;
}

function useLegacyGetTemps({ args }: DailyTempProps) {
	const client = useApolloClient();
	const [data, setData] = useState<DailyTempItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.query<DailyTempResponse>({
				query: GET_DAILYTEMPERATURES,
				variables: args,
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only",
			});

			const temps = fetchedData?.getDailyTemperature ?? [];
			setData(temps);
			if (temps.length === 0) {
				setError("No data available");
			}
		} catch (err: any) {
			console.error("Temp fetch error:", err);
			setError(err?.message || err?.networkError?.message || "Network error");
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return { data, loading, error, fetchData };
}

function formatTemp(value: number): string {
	return `${value / 100} ℃`;
}

function TempViewBlock({ ConnectionArgs }: TempViewBlock_Props) {
	const args = useMemo<GetDailyTempArgs>(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		lastDays: 15,
	}), [ConnectionArgs]);

	const { data: Temps, error, loading, fetchData } = useLegacyGetTemps({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={fetchData}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
					}}
				/>
				<frames.Graph_QL_header>
					<h2>Daily Temperatures</h2>
				</frames.Graph_QL_header>

				<frames.GraphQLTables>
					<table>
						<thead>
							<tr>
								<th>Date:</th>
								<th>Avg:</th>
								<th>Count:</th>
								<th>Min:</th>
								<th>Max:</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
										Loading...
									</td>
								</tr>
							) : error ? (
								<tr>
									<td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: '#880000' }}>
										{error}
									</td>
								</tr>
							) : Temps && Temps.length > 0 ? (
								Temps.map((Temp, index) => {
									return (
										<tr key={index}>
											<td>{formatLogTimestamp(Temp.timestamp)}</td>
											<td>{formatTemp(Temp.value1)}</td>
											<td>{Temp.value2}</td>
											<td>{formatTemp(Temp.value3)}</td>
											<td>{formatTemp(Temp.value4)}</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: '#880000' }}>
										No data available
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</frames.GraphQLTables>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | All RFIDS
interface AllRfidViewBlock_Props {
	ConnectionArgs: { domain: string; gate: number; device: number };
}

interface AllRfidProps {
	args: GetAllRfidArgs;
}

function useLegacyGetAllRfids({ args }: AllRfidProps) {
	const client = useApolloClient();
	const [data, setData] = useState<AllRfidItem[] | undefined>();
	const [loading, setLoading] = useState<boolean>();
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.query<AllRfidResponse>({
				query: GET_ALLRFIDS,
				variables: args,
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",
			});

			if (!fetchedData.getRfids || fetchedData.getRfids.length === 0) {
				setError("No data found");
				setData(undefined);
				return;
			}

			setData(fetchedData.getRfids);
		} catch (err: any) {
			const message =
				err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	useEffect(() => {
		fetchData(); // initial load
	}, [fetchData]);
	return { data, loading, error, fetchData };
}

// --- COMPONENT ---

function AllRfidViewBlock({
	ConnectionArgs,
	setViewType,
	setSelectedRfid,
}: {
	ConnectionArgs: { domain: string; gate: number; device: number };
	setViewType: React.Dispatch<React.SetStateAction<string>>;
	setSelectedRfid: React.Dispatch<React.SetStateAction<string | null>>;
}) {
	const args = useMemo(
		() => ({
			domain: ConnectionArgs.domain,
			gate: ConnectionArgs.gate,
			device: ConnectionArgs.device,
			lastDays: 15,
			fetch: true,
		}),
		[ConnectionArgs]
	);

	const { data, error, loading, fetchData } = useLegacyGetAllRfids({ args });

	const [searchTerm, setSearchTerm] = useState("");

	const filteredData = useMemo(() => {
		if (!data) return [];
		return data.filter(
			(item) =>
				item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
				String(item.value).includes(searchTerm)
		);
	}, [data, searchTerm]);

	return (
		<>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={fetchData}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: {
							height: "30px",
							width: "fit-content",
							position: "absolute",
						},
					}}
				/>
				<frames.Graph_QL_header>
					<h2>All Rfids</h2>
				</frames.Graph_QL_header>

				<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "10px" }}>
					<NewInputList
						placeholders={["Search by ID or Number..."]}
						onChanges={[setSearchTerm]}
						valueOverrides={{
							defaultValues: [searchTerm],
							valueTypes: ["text"],
						}}
						isRequired={[false]}
						fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
						listOverrides={{ listStyle: ListStyle_Types.vertical }}
					/>
				</div>

				<frames.GraphQLTables>
					<div
						style={{
							height: "auto",
							maxHeight: "55vh",
							overflowY: "auto",
							overflowX: "auto",
							width: "100%",
						}}
					>
						<table>
							<thead>
								<tr>
									<th>ID</th>
									<th>Number</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td
											colSpan={3}
											style={{ textAlign: "center", padding: "1rem" }}
										>
											Loading...
										</td>
									</tr>
								) : error ? (
									<tr>
										<td
											colSpan={3}
											style={{
												textAlign: "center",
												padding: "1rem",
												color: "#880000",
											}}
										>
											{error}
										</td>
									</tr>
								) : filteredData.length === 0 ? (
									<tr>
										<td
											colSpan={3}
											style={{
												textAlign: "center",
												padding: "1rem",
												color: "#555",
											}}
										>
											No matching results
										</td>
									</tr>
								) : (
									filteredData.map((item, index) => (
										<tr key={index}>
											<td>{item.id}</td>
											<td>{item.value}</td>
											<td>
												<NewButton
													onClick={() => {
														setSelectedRfid(item.id);
														setViewType("dailyrfid");
													}}
													overrides={{
														baseStyles: {
															height: "25px",
															width: "fit-content",
														},
													}}
													content="View"
													endEnhancer={<>{glyphs.Animal({})}</>}
												/>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</frames.GraphQLTables>
			</frames.Graph_QL_sections>
		</>
	);
}
// #endregion

// #region | RFID Scales
interface DailyScalesByRFIDViewBlock_Props {
	ConnectionArgs: { domain: string; gate: number; device: number };
	initialRfid?: string | null;
}

interface DailyScalesByRFIDProps {
	args: GetDailyScalesByRFIDArgs;
}

function useLegacyGetDailyScalesByRFID({ args }: DailyScalesByRFIDProps) {
	const client = useApolloClient();
	const [data, setData] = useState<DailyScalesByRFIDItem[] | undefined>();
	const [loading, setLoading] = useState<boolean>();
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.query<DailyScalesByRFIDResponse>({
				query: GET_DAILYSCALESBYRFID,
				variables: args,
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",
			});

			if (!fetchedData.getDailyScalesByRFID || fetchedData.getDailyScalesByRFID.length === 0) {
				setError("No data found");
				setData(undefined);
				return;
			}

			setData(fetchedData.getDailyScalesByRFID);
		} catch (err: any) {
			const message =
				err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, fetchData };
}

// --- COMPONENT ---

function DailyScalesByRFIDViewBlock({ ConnectionArgs, initialRfid, }: DailyScalesByRFIDViewBlock_Props) {
	const [rfid, setRfid] = useState<string>(initialRfid ?? "");
	const args = useMemo(
		() => ({
			domain: ConnectionArgs.domain,
			gate: ConnectionArgs.gate,
			device: ConnectionArgs.device,
			lastDays: null,
			rfid,
			fetch: true,
		}),
		[ConnectionArgs, rfid]
	);

	const { data, error, loading, fetchData } = useLegacyGetDailyScalesByRFID({ args });
	const [displayLimit] = useState(5);

	useEffect(() => {
		if (initialRfid) {
			fetchData();
		}
	}, [initialRfid, fetchData]);

	return (
		<div>
			<frames.Graph_QL_sections>
				<NewButton
					onClick={fetchData}
					content={loading ? "Loading..." : "Fetch"}
					disabled={loading}
					overrides={{
						baseStyles: { height: "30px", width: "fit-content", position: "absolute" }
					}}
				/>
				<frames.Graph_QL_header>
					<h2>Daily Scales By RFID</h2>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: "10px" }}>
						<label>ID:</label>
						<NewInputList
							placeholders={["Enter RFID"]}
							onChanges={[setRfid]}
							valueOverrides={{
								defaultValues: [rfid],
								valueTypes: ["text"],
							}}
							isRequired={[true]}
							fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
							listOverrides={{ listStyle: ListStyle_Types.vertical }}
						/>
						{/*<Button
              onClick={fetchData}
              content="Search"
              overrides={{ baseButton: { height: "30px", width: "fit-content" } }}
            />*/}
					</div>
				</frames.Graph_QL_header>

				<frames.GraphQLTables>
					<table>
						<thead>
							<tr>
								<th>Date</th>
								<th>Avg</th>
								<th>Count</th>
								<th>Min</th>
								<th>Max</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={5} style={{ textAlign: "center", padding: "1rem" }}>Loading...</td>
								</tr>
							) : error ? (
								<tr>
									<td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "#880000" }}>
										{error}
									</td>
								</tr>
							) : data?.length ? (
								data.slice(0, displayLimit).map((item, index) => {
									let time = "";
									const tsStr = item.timestamp?.toString();
									time = isNaN(Number(tsStr))
										? (item.timestamp as Date).toLocaleString()
										: new Date(Number(tsStr) * 1000).toLocaleString();

									return (
										<tr key={index}>
											<td>{time}</td>
											<td>{item.value1}</td>
											<td>{item.value2}</td>
											<td>{item.value3}</td>
											<td>{item.value4}</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: "#880000" }}>
										No results found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</frames.GraphQLTables>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Firmware
interface ChangeFirmwareBlock_Props {
	ChangeFirmwareData?: [string, ChangeFirmwareItem],
	ConnectionArgs: { domain: string, gate: number, device: number }
}
interface ChangeFirmwareProps { args: GetChangeFirmwareArgs }

function useLegacyChangeFirmware({ args }: ChangeFirmwareProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeFirmwareItem | undefined>(); // single object
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const updateFirmware = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			setData(undefined);

			const { data: updated } = await client.mutate<ChangeFirmwareResponse>({
				mutation: GET_CHANGEFIRMWARE,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					firmwarenumber: args.firmwarenumber,
				},
				context: { clientName: "legacy" },
			});

			if (!updated?.changeFirmware) {
				setData(undefined);
				setError("No data found");
			} else {
				if (updated.changeFirmware.success) {
					setData(updated.changeFirmware);
				} else {
					setError(updated.changeFirmware.message);
				}
			}

		} catch (err: any) {
			setError(err?.message || err?.networkError?.message || "Unknown error occurred");
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, updateFirmware };
}


function ChangeFirmwareBlock({ ChangeFirmwareData, ConnectionArgs }: ChangeFirmwareBlock_Props) {
	const [firmwarenumber, setFirmwareNumber] = useState<number>(0);

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		firmwarenumber
	}), [ConnectionArgs, firmwarenumber]);

	const { data, error, loading, updateFirmware } = useLegacyChangeFirmware({ args });

	const {
		data: binsData,
		loading: binsLoading,
		error: binsError,
		refetch: refetchBins,
	} = useQuery<AvailableBinsResponse>(GET_AVAILABLE_BINS, {
		context: { clientName: "legacy" },
		fetchPolicy: "network-only",
	});

	const { data: configData, loading: configLoading, error: configError, refetch: handleRefetchConfig } =
		useQuery<StartConfigsResponse, GetStartConfigsArgs>(GetStartConfigs, {
			variables: { domain: ConnectionArgs.domain, gatewayindex: ConnectionArgs.gate, mapdeviceindex: ConnectionArgs.device },
			context: { clientName: "legacy" },
			fetchPolicy: "no-cache",
			notifyOnNetworkStatusChange: true
		});
	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change Firmware</h2>
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<NewCreatableSelect
							placeholder="New firmware number"
							onChange={(selectedOption) => {
								const parsed = parseInt(selectedOption?.value ?? "");
								setFirmwareNumber(isNaN(parsed) ? 0 : parsed);
							}}
							options={
								binsData?.getAvailableBins?.map((bin) => ({
									label: String(bin),
									value: String(bin),
								})) ?? []
							}
							isRequired={false}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							isSearchable={true}
						/>
						<NewButton
							onClick={() => updateFirmware()}
							disabled={loading}
							content={loading ? "Updating..." : "Update Firmware"}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content" }
							}}
						/>
					</div>
				</frames.Graph_QL_header>
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
					<div style={{ width: "100%", maxWidth: "600px", marginTop: "0.5rem" }}>
						<NewButton
							onClick={() => refetchBins()}
							content={binsLoading ? "Loading..." : "Fetch Available Bins"}
							disabled={binsLoading}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content", position: "absolute", marginTop: "1rem" }
							}}
						/>
						<frames.Graph_QL_header>
							<h3>Available Bins</h3>
						</frames.Graph_QL_header>

						{binsLoading && <p>Loading bins...</p>}
						{binsError && <p style={{ color: "#880000" }}>{binsError.message}</p>}
						{binsData?.getAvailableBins?.length
							? <frames.GraphQLTables><table style={{ maxWidth: "600px" }}>
								{binsData.getAvailableBins.map((bin, index) => (
									<tr key={index}>{JSON.stringify(bin)}</tr>
								))}
							</table></frames.GraphQLTables>
							: !binsLoading && <p>No bins available</p>}
					</div>
				</div>
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "1rem" }}>
					<NewButton
						onClick={() => handleRefetchConfig({ domain: ConnectionArgs.domain, gatewayindex: ConnectionArgs.gate, mapdeviceindex: ConnectionArgs.device })}
						content={configLoading ? "Loading version..." : "Refresh Version"}
						disabled={configLoading}
						overrides={{
							baseStyles: { height: "30px", width: "fit-content" }
						}}
					/>
					{configData?.getStartConfigs?.[0]?.value ?
						(() => {
							try {
								const parsed = JSON.parse(configData.getStartConfigs[0].value);
								return <div>Current Firmware Version: {parsed.v}</div>;
							} catch (e) {
								return <div>Error parsing firmware version</div>;
							}
						})()
						: <div>Current Firmware Version: unknown </div>
					}

					{loading && <LoadingSpinner />}
					{error && <p style={{ color: "#880000" }}>{error}</p>}
					{data && <p style={{ color: colors.systemGreen }}>Success: {data.message}</p>}
				</div>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | VM Software
interface ChangeVmSoftwareBlock_Props {
	ChangeVmSoftwareData?: [string, ChangeVmSoftwareItem],
	ConnectionArgs: { domain: string; gate: number; device: number; },
	CallArgs: { filename: string }
}
interface ChangeVmSoftwareProps { args: GetChangeVmSoftwareArgs }

function useLegacyChangeVmSoftware({ args }: ChangeVmSoftwareProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeVmSoftwareItem | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setData(undefined);
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.mutate<ChangeVmSoftwareResponse>({
				mutation: GET_CHANGEVMSOFTWARE,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					filename: args.filename
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			if (!fetchedData?.changeVmSoftware) {
				setData(undefined);
				setError("No data found");
			} else {
				if (fetchedData.changeVmSoftware.success) {
					setData(fetchedData.changeVmSoftware);
				} else {
					setError(fetchedData.changeVmSoftware.message);
				}
			}

		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);


	return { data, loading, error, fetchData };
}

function ChangeVmSoftwareBlock({ ChangeVmSoftwareData, ConnectionArgs, CallArgs }: ChangeVmSoftwareBlock_Props) {
	const [filename, setFilename] = useState<string>("");

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		filename
	}), [ConnectionArgs, filename]);

	const { data, error, loading, fetchData } = useLegacyChangeVmSoftware({ args });
	const {
		data: VmsData,
		loading: VmLoading,
		error: VmsError,
		refetch: refetchVms,
	} = useQuery<AvailableVmsResponse>(GET_AVAILABLE_VMS, {
		context: { clientName: "legacy" },
		fetchPolicy: "network-only",
	});
	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change VM Software</h2>
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>

						<NewCreatableSelect
							placeholder="New VM Software"
							onChange={(selectedOption) => {
								const parsed = parseInt(selectedOption?.value ?? "");
								setFilename(selectedOption?.value || "");
							}}
							options={
								VmsData?.getAvailableVms.map((vm, index) => ({
									label: String(vm),
									value: String(vm),
								})) ?? []
							}
							isRequired={false}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							isSearchable={true}
						/>

						<NewButton
							onClick={() => fetchData()}
							disabled={loading}
							content={loading ? "Updating..." : "Update VM Software"}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content" }
							}}
						/>
					</div>
				</frames.Graph_QL_header>
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
					<div style={{ width: "100%", maxWidth: "600px", marginTop: "0.5rem" }}>
						<NewButton
							onClick={() => refetchVms()}
							content={VmLoading ? "Loading..." : "Fetch Available VMs"}
							disabled={VmLoading}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content", position: "absolute", marginTop: "1rem" }
							}}
						/>
						<frames.Graph_QL_header>
							<h3>Available VMs</h3>
						</frames.Graph_QL_header>
						{VmLoading && <p>Loading VMs...</p>}
						{VmsError && <p style={{ color: "#880000" }}>{VmsError.message}</p>}
						{VmsData?.getAvailableVms?.length
							? <frames.GraphQLTables><table>
								{VmsData.getAvailableVms.map((vm, index) => (
									<tr key={index}>{JSON.stringify(vm)}</tr>
								))}
							</table>
							</frames.GraphQLTables>
							: !VmLoading && <p>No VMs available</p>}

						{loading && <LoadingSpinner />}
						{error && <p style={{ color: "#880000" }}>{error}</p>}
						{data && <p style={{ color: colors.systemGreen }}>Success: {data.message}</p>}
					</div>
				</div>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Settings
interface ChangeGlobalSettingsBlock_Props {
	ChangeGlobalSettingsData?: [string, ChangeGlobalSettingsItem],
	ConnectionArgs: { domain: string, gate: number, device: number }
}
interface ChangeGlobalSettingsProps { args: GetChangeGlobalSettingsArgs }

function useLegacyChangeGlobalSettings({ args }: ChangeGlobalSettingsProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeGlobalSettingsItem[] | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			setSuccess(null);

			const { data: fetchedData } = await client.mutate<ChangeGlobalSettingsResponse>({
				mutation: GET_CHANGEGLOBALSETTINGS,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					value: args.value
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			const result = fetchedData?.setGlobalSettings;

			if (!result) {
				setError("No response from server");
			} else if (result.success) {
				setSuccess("success");
			} else {
				setError(result.message || "Failed to update settings");
			}

		} catch (err: any) {
			setError(err?.message || "Unknown error occurred");
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { loading, error, success, fetchData };
}

function ChangeGlobalSettingsBlock({ ChangeGlobalSettingsData, ConnectionArgs }: ChangeGlobalSettingsBlock_Props) {

	// Label ↔ colindex mapping
	const labelMap = [
		{ label: "Scale ID", colindex: 0 },
		{ label: "Weight Reset", colindex: 1 },
		{ label: "Temp Sensor Val", colindex: 2 },
		{ label: "Water Sensor Val", colindex: 3 },
	];

	// Load current settings from backend
	const [getGlobalSettings, { data: GGSData, loading: GGSLoading, error: GGSError }] =
		useLazyQuery<GetGlobalSettingsResponse>(GET_GETGLOBALSETTINGS, {
			context: { clientName: "legacy" },
			fetchPolicy: "network-only",
		});


	const [plcDataset, setPlcDataset] = useState<Plc_Dataset[]>([
		{ rowindex: 0, colindex: 0, colvalue: 1 },
		{ rowindex: 0, colindex: 1, colvalue: 1000 },
		{ rowindex: 0, colindex: 2, colvalue: 50 },
		{ rowindex: 0, colindex: 3, colvalue: 0 },
	]);

	// Prepare fetch args
	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		value: plcDataset,
	}), [ConnectionArgs, plcDataset]);

	const { error, success, loading, fetchData } = useLegacyChangeGlobalSettings({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change Global Settings</h2>

					{/* Editable Input Fields */}
					{labelMap.map(({ label, colindex }) => {
						const datasetItem = plcDataset.find(item => item.colindex === colindex);
						const value = datasetItem?.colvalue ?? "";

						return (
							<div key={colindex} style={{ marginBottom: "0.5rem" }}>
								<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
									<label style={{ display: "block", marginBottom: "0.25rem" }}>{label}</label>
									<NewInputList
										placeholders={[label]}
										onChanges={[
											(newValue: string) => {
												const num = parseInt(newValue);
												setPlcDataset(prev =>
													prev.map(item =>
														item.colindex === colindex
															? { ...item, colvalue: isNaN(num) ? 0 : num }
															: item
													)
												);
											},
										]}
										valueOverrides={{
											defaultValues: [value.toString()],
											valueTypes: ["number"],
										}}
										isRequired={[true]}
										fieldOverrides={{
											fieldStyle: NewFieldStyles.greyTertiary,
										}}
										listOverrides={{
											listStyle: ListStyle_Types.vertical,
										}}
									/>
								</div>
							</div>
						);
					})}

					<NewButton
						onClick={async () => {
							await fetchData();
						}} disabled={loading}
						content={loading ? "Updating..." : "Update Global Settings"}
						overrides={{
							baseStyles: { height: "30px", width: "fit-content" }
						}}
					/>
					{success && <p style={{ color: "green", marginTop: "0.5rem" }}>{success}</p>}
					{error && <p style={{ color: "#880000", marginTop: "0.5rem" }}>{error}</p>}

				</frames.Graph_QL_header>
				{/* Display Current Settings */}
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", }}>
					<div style={{ width: "100%", maxWidth: "600px", marginTop: "0.5rem" }}>

						<NewButton
							onClick={() =>
								getGlobalSettings({
									variables: {
										mapdeviceindex: ConnectionArgs.device,
										domain: ConnectionArgs.domain,
										gatewayindex: ConnectionArgs.gate,
									},
								})
							}
							content={GGSLoading ? "Loading..." : "Fetch"}
							disabled={GGSLoading}
							overrides={{
								baseStyles: { height: "30px", width: "fit-content", position: "absolute", marginTop: "1rem" }
							}}
						/>
						<frames.Graph_QL_header>
							<h3>Current Settings</h3>
						</frames.Graph_QL_header>

						{GGSLoading && <p>Loading current settings...</p>}
						{GGSError && <p style={{ color: "#880000" }}>{GGSError.message}</p>}
						{!GGSLoading && (
							<frames.GraphQLTables>
								<table>
									<tbody>
										{labelMap.map(({ label, colindex }) => {
											const value = GGSData?.getGlobalSettings.find(g => g.colindex === colindex + 10)?.colvalue ?? "—";
											return (
												<tr key={colindex}>
													<th>{label}</th>
													<td>{value}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</frames.GraphQLTables>
						)}
					</div>
				</div>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Weight Limits
interface SetWeightLimitsBlock_Props {
	setweightlimitsData?: [string, SetWeightLimitsItem],
	ConnectionArgs: { domain: string; gate: number; device: number; },
}
interface SetWeightlimitsProps { args: GetSetWeightLimitsArgs }


function useLegacySetWeightlimits({ args }: SetWeightlimitsProps) {
	const client = useApolloClient();
	const [data, setData] = useState<SetWeightLimitsItem | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			setSuccess(null);

			const { data: fetchedData } = await client.mutate<SetWeightLimitsResponse>({
				mutation: GET_SETWEIGHTLIMITS,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					mapdeviceindex: args.device,
					value: args.value,
					fullTable: args.fullTable
				},
				context: { clientName: "legacy" },
				fetchPolicy: "network-only",
			});

			const result = fetchedData?.setWeightLimits;

			if (!result) {
				setData(null);
				setError("No data returned");
			} else if (result.success) {
				setSuccess("Success");
				setData(result);
			} else {
				setError(result.message || "Operation failed");
			}
		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, success, error, fetchData };
}

function SetWeightLimitsBlock({ setweightlimitsData, ConnectionArgs }: SetWeightLimitsBlock_Props) {
	const [value, setvalue] = useState<WeightLimits[]>([]);


	const [WeightLimits, _setWeightLimits] = useState<WeightLimits[]>([
		{
			"CurveNo": 1, "Name": "+30%-20%",
			"Weight": 4000, "MinWeight": 3200, "MaxWeight": 5200,
			"Reset": 1000, "ScaleTime": 2500
		},
		{
			"CurveNo": 1, "Name": "+30%-20%",
			"Weight": 40000, "MinWeight": 32000, "MaxWeight": 52000,
			"Reset": 2000, "ScaleTime": 4000
		},
		{
			"CurveNo": 1, "Name": "+30%-20%",
			"Weight": 200000, "MinWeight": 160000, "MaxWeight": 260000,
			"Reset": 5000, "ScaleTime": 5000
		},
		{
			"CurveNo": 2, "Name": "Finisher",
			"Weight": 20000, "MinWeight": 15000, "MaxWeight": 35000,
			"Reset": 5000, "ScaleTime": 5000
		},
		{
			"CurveNo": 2, "Name": "Finisher",
			"Weight": 30000, "MinWeight": 22000, "MaxWeight": 37000,
			"Reset": 5000, "ScaleTime": 5000
		},
		{
			"CurveNo": 2, "Name": "Finisher",
			"Weight": 100000, "MinWeight": 85000, "MaxWeight": 115000,
			"Reset": 5000, "ScaleTime": 5000
		},
		{
			"CurveNo": 2, "Name": "Finisher",
			"Weight": 135000, "MinWeight": 110000, "MaxWeight": 165000,
			"Reset": 5000, "ScaleTime": 5000
		},
		{
			"CurveNo": 2, "Name": "Finisher",
			"Weight": 170000, "MinWeight": 140000, "MaxWeight": 205000,
			"Reset": 5000, "ScaleTime": 5000
		},
	]
	);
	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		device: ConnectionArgs.device,
		value: WeightLimits,
		fullTable: true,
	}), [ConnectionArgs, WeightLimits]);

	const { data, error, success, loading, fetchData: setWeightLimits } = useLegacySetWeightlimits({ args });
	const [fetchingSWL, setFetchingSWL] = useState(false);

	useEffect(() => {
		setvalue(WeightLimits);
	}, [WeightLimits]);

	const {
		data: SWLData,
		loading: SWLLoading,
		error: SWLError,
		refetch: refetchSWL
	} = useQuery<TableFromDeviceResponse>(GET_TABLE_FROM_DEVICE, {
		context: { clientName: "legacy" },
		fetchPolicy: "network-only",
		variables: {
			domain: ConnectionArgs.domain,
			gatewayindex: ConnectionArgs.gate,
			mapdeviceindex: ConnectionArgs.device,
			maptableindex: 120,
		},
	});

	return (
		<frames.Graph_QL_sections>
			<frames.Graph_QL_header>
				<h2>Change Weight Limits</h2>
				<div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
					<NewButton
						onClick={async () => {
							setFetchingSWL(true);
							try {
								await setWeightLimits();
								await refetchSWL();
							} catch (err) {
								console.error(err);
							} finally {
								setFetchingSWL(false);
							}
						}}
						disabled={loading}
						content={loading ? "Loading..." : "Change"}
					/>
				</div>
				{success && <p style={{ color: "green", textAlign: "center", marginTop: "1rem" }}>{success}</p>}
				{error && <p style={{ color: "#880000", textAlign: "center", marginTop: "1rem" }}>{error}</p>}
			</frames.Graph_QL_header>
			<NewButton
				onClick={() => refetchSWL()}
				content={SWLLoading ? "Loading..." : "Fetch"}
				disabled={SWLLoading}
				overrides={{
					baseStyles: { height: "30px", width: "fit-content", marginTop: "1rem" }
				}}
			/>
			{SWLLoading && <p>Loading device weight limits...</p>}
			{SWLError && <p style={{ color: "#880000" }}>{SWLError.message}</p>}

			<frames.Graph_QL_sections>
				{SWLData?.getTableFromDevice ? (
					SWLData.getTableFromDevice.some(item => item.colvalue === "TimeOut") ? (
						<p style={{ textAlign: "center", marginTop: "1rem", color: "#880000" }}>
							Device did not respond (TimeOut)
						</p>
					) : (
						<frames.GraphQLTables>
							<table style={{ maxWidth: "800px", borderCollapse: "collapse" }}>
								<thead>
									<tr>
										<th>Row</th>
										<th>CurveNo</th>
										<th>Name</th>
										<th>Weight</th>
										<th>MinWeight</th>
										<th>MaxWeight</th>
										<th>ScaleTime</th>
										<th>Reset</th>
									</tr>
								</thead>
								<tbody>
									{Object.values(
										SWLData.getTableFromDevice
											.filter(item => item.rowindex >= 0)
											.reduce((acc, item) => {
												if (!acc[item.rowindex]) acc[item.rowindex] = {};
												acc[item.rowindex][item.colindex] = item.colvalue;
												return acc;
											}, {} as Record<number, Record<number, string | number>>)
									).map((row, rowIdx) => (
										<tr key={rowIdx}>
											<td>{rowIdx}</td>
											<td>{row[1] ?? "—"}</td>
											<td>{row[2] ?? "—"}</td>
											<td>{row[3] ?? "—"}</td>
											<td>{row[4] ?? "—"}</td>
											<td>{row[5] ?? "—"}</td>
											<td>{row[6] ?? "—"}</td>
											<td>{row[7] ?? "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</frames.GraphQLTables>
					)
				) : null}
			</frames.Graph_QL_sections>
		</frames.Graph_QL_sections>
	);
}
// #endregion

// #region | Change MDI
interface ChangeMdiBlock_Props {
	ConnectionArgs: { domain: string; gate: number; device: number; };
	macFromConfig?: string | null;
}
interface setChangeMdiProps { args: GetChangeMdiArgs }
function useLegacyChangeMdi({ args }: setChangeMdiProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeMdiItem | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);


	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.mutate<ChangeMdiResponse>({
				mutation: GET_CHANGEMDI,
				variables: {
					domain: args.domain,
					gatewayindex: args.gate,
					oldmapdeviceindex: args.oldmapdeviceindex,
					newmapdeviceindex: args.newmapdeviceindex,
					macadress: args.macadress,
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			if (!fetchedData || !fetchedData.changeMdi) {
				setData(undefined);
				setError("No data found");
			} else {
				setData(fetchedData.changeMdi);
			}

		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, fetchData };
}

function ChangeMdiBlock({ ConnectionArgs, macFromConfig }: ChangeMdiBlock_Props) {
	const [oldMdi, setOldMdi] = useState<number>(ConnectionArgs.device);
	const [newMdi, setNewMdi] = useState<number>(0);
	const [macadress, setMacadress] = useState<string>("");

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gate: ConnectionArgs.gate,
		oldmapdeviceindex: oldMdi,
		newmapdeviceindex: newMdi,
		macadress: macadress
	}), [ConnectionArgs, oldMdi, newMdi, macadress]);

	useEffect(() => {
		setMacadress(macFromConfig ?? "");
	}, [macFromConfig]);

	const { data, error, loading, fetchData } = useLegacyChangeMdi({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change MDI</h2>
					<div>
						{/* Old MDI */}
						<label>
							Old MDI: {oldMdi}
						</label>
					</div>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* New MDI */}
						<label>
							New MDI:
						</label>

						<NewInputList
							placeholders={["New MDI"]}
							onChanges={[
								(value: string) => setNewMdi(Number(value))
							]}
							valueOverrides={{
								defaultValues: [newMdi.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					{/* MAC Address */}
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<label>MAC Address:</label>
						<NewInputList
							placeholders={["MAC Address"]}
							onChanges={[
								(value: string) => setMacadress(value)
							]}
							/*valueOverrides={{
								defaultValues: [macadress]
							}}*/
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					<NewButton
						onClick={fetchData}
						disabled={loading}
						content={loading ? "Loading..." : "Change MDI"}
						overrides={{
							baseStyles: { marginTop: "1rem", height: "30px", width: "fit-content" }
						}}
					/>
					{error && <p style={{ color: "#880000" }}>{error}</p>}

					{data && (
						<div>
							<p>{data.code} - {data.message}</p>
						</div>
					)}
				</frames.Graph_QL_header>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Change Gate
interface ChangeGateBlock_Props {
	changeGateData?: [string, ChangeGateItem],
	ConnectionArgs: { domain: string; gate: number; };
	macFromConfig?: string | null;
}
interface setChangeGateProps { args: GetChangeGateArgs }
function useLegacyChangeGate({ args }: setChangeGateProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeGateItem | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.mutate<ChangeGateResponse>({
				mutation: GET_CHANGEGATE,
				variables: {
					domain: args.domain,
					oldgatewayindex: args.oldgateindex,
					newgatewayindex: args.newgateindex,
					macadress: args.macadress,
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			if (!fetchedData || !fetchedData.changeGate) {
				setData(undefined);
				setError("No data found");
			} else {
				setData(fetchedData.changeGate);
			}

		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, fetchData };
}
function ChangeGateBlock({ ConnectionArgs, macFromConfig }: ChangeGateBlock_Props) {
	const [oldGate, setOldGate] = useState<number>(ConnectionArgs.gate);
	const [newGate, setNewGate] = useState<number>(0);
	const [macadress, setMacadress] = useState<string>("");

	useEffect(() => {
		setMacadress(macFromConfig ?? "");
	}, [macFromConfig]);

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		oldgateindex: oldGate,
		newgateindex: newGate,
		macadress: macadress
	}), [ConnectionArgs, oldGate, newGate, macadress]);

	const { data, error, loading, fetchData } = useLegacyChangeGate({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>
						Change Gate
					</h2>

					{/* Old Gate */}
					<label>
						Old Gate: {oldGate}
					</label>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* New Gate */}
						<label>
							New Gate:
						</label>
						<NewInputList
							placeholders={["New Gate"]}
							onChanges={[
								(value: string) => setNewGate(Number(value))
							]}
							valueOverrides={{
								defaultValues: [newGate.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					{/* MAC Address */}
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<label>MAC Address:</label>
						<NewInputList
							placeholders={["MAC Address"]}
							onChanges={[
								(value: string) => setMacadress(value)
							]}
							/*valueOverrides={{
								defaultValues: [macadress]
							}}*/
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>



					<NewButton
						onClick={fetchData}
						disabled={loading}
						content={loading ? "Loading..." : "Change Gate"}
						overrides={{
							baseStyles: { marginTop: "1rem", height: "30px", width: "fit-content" }
						}}
					/>

					{error && <p style={{ color: "#880000" }}>{error}</p>}

					{data && (
						<div>
							<p>{data.code} - {data.message}</p>
						</div>
					)}
				</frames.Graph_QL_header>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Change Domain
interface ChangeDomainBlock_Props {
	changeDomainData?: [string, ChangeDomainItem],
	ConnectionArgs: { domain: string; gate: number; };
	macFromConfig?: string | null;
}
interface setChangeDomainProps { args: GetChangeDomainArgs }
function useLegacyChangeDomain({ args }: setChangeDomainProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeDomainItem | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.mutate<ChangeDomainResponse>({
				mutation: GET_CHANGEDOMAIN,
				variables: {
					domain: args.domain,
					gatewayindex: args.gatewayindex,
					newdomain: args.newdomain,
					newpin: args.newpin,
					macadress: args.macadress,
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			if (!fetchedData || !fetchedData.changeDomain) {
				setData(undefined);
				setError("No data found");
			} else {
				setData(fetchedData.changeDomain);
			}

		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, fetchData };
}
function ChangeDomainBlock({ ConnectionArgs, macFromConfig }: ChangeDomainBlock_Props) {
	const [newPin, setNewPin] = useState<number>(0)
	const [newDomain, setNewDomain] = useState<string>("");
	const [macadress, setMacadress] = useState<string>("");

	useEffect(() => {
		setMacadress(macFromConfig ?? "");
	}, [macFromConfig]);

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gatewayindex: ConnectionArgs.gate,
		newdomain: newDomain + "|" + newPin + "|" + macadress,
		newpin: newPin,
		macadress: macadress
	}), [ConnectionArgs, newDomain, newPin, macadress]);

	const { data, error, loading, fetchData } = useLegacyChangeDomain({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change Domain</h2>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* new domain name */}
						<label>
							New Domain name:
						</label>
						<NewInputList
							placeholders={["New Domain"]}
							onChanges={[
								(value: string) => setNewDomain(value)
							]}
							valueOverrides={{
								defaultValues: [newDomain.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* New pin */}
						<label>
							New Pin:
						</label>

						<NewInputList
							placeholders={["New Pin"]}
							onChanges={[
								(value: string) => setNewPin(Number(value))
							]}
							valueOverrides={{
								defaultValues: [newPin.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					{/* MAC Address */}
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<label>MAC Address:</label>
						<NewInputList
							placeholders={["MAC Address"]}
							onChanges={[
								(value: string) => setMacadress(value)
							]}
							/*valueOverrides={{
								defaultValues: [macadress]
							}}*/
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>


					<NewButton
						onClick={fetchData}
						disabled={loading}
						content={loading ? "Loading..." : "Change Domain"}
						overrides={{
							baseStyles: { marginTop: "1rem", height: "30px", width: "fit-content" }
						}}
					/>

					{error && <p style={{ color: "#880000" }}>{error}</p>}

					{data && (
						<div>
							<p>{data.code} - {data.message}</p>
						</div>
					)}
				</frames.Graph_QL_header>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

// #region | Change IP
interface ChangeIpBlock_Props {
	changeIPData?: [string, ChangeIpItem],
	ConnectionArgs: { domain: string; gate: number; };
	macFromConfig?: string | null;
}
interface setChangeIPProps { args: GetChangeIpArgs }
function useLegacyChangeIp({ args }: setChangeIPProps) {
	const client = useApolloClient();
	const [data, setData] = useState<ChangeIpItem | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const { data: fetchedData } = await client.mutate<ChangeIpResponse>({
				mutation: GET_CHANGEIP,
				variables: {
					domain: args.domain,
					gatewayindex: args.gatewayindex,
					newdomain: args.newdomain,
					newpin: args.newpin,
					macadress: args.macadress,
				},
				context: { clientName: 'legacy' },
				fetchPolicy: "network-only"
			});

			if (!fetchedData || !fetchedData.changeIp) {
				setData(undefined);
				setError("No data found");
			} else {
				setData(fetchedData.changeIp);
			}

		} catch (err: any) {
			const message = err?.message || err?.networkError?.message || "Unknown error occurred";
			setError(message);
		} finally {
			setLoading(false);
		}
	}, [client, args]);

	return { data, loading, error, fetchData };
}
function ChangeIpBlock({ ConnectionArgs, macFromConfig }: ChangeIpBlock_Props) {
	const [newPin, setNewPin] = useState<number>(0)
	const [newDomain, setNewDomain] = useState<string>("");
	const [macadress, setMacadress] = useState<string>("");

	useEffect(() => {
		setMacadress(macFromConfig ?? "");
	}, [macFromConfig]);

	const args = useMemo(() => ({
		domain: ConnectionArgs.domain,
		gatewayindex: ConnectionArgs.gate,
		newdomain: newDomain + "|" + newPin + "|" + macadress,
		newpin: newPin,
		macadress: macadress
	}), [ConnectionArgs, newDomain, newPin, macadress]);

	const { data, error, loading, fetchData } = useLegacyChangeIp({ args });

	return (
		<div>
			<frames.Graph_QL_sections>
				<frames.Graph_QL_header>
					<h2>Change IP</h2>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* New IP */}
						<label>
							New IP:
						</label>
						<NewInputList
							placeholders={["New IP"]}
							onChanges={[
								(value: string) => setNewDomain(value)
							]}
							valueOverrides={{
								defaultValues: [newDomain.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						{/* New Pin */}
						<label>
							New Pin:
						</label>
						<NewInputList
							placeholders={["New Pin"]}
							onChanges={[
								(value: string) => setNewPin(Number(value))
							]}
							valueOverrides={{
								defaultValues: [newPin.toString()]
							}}
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>

					{/* MAC Address */}
					<div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
						<label>MAC Address:</label>
						<NewInputList
							placeholders={["MAC Address"]}
							onChanges={[
								(value: string) => setMacadress(value)
							]}
							/*valueOverrides={{
								defaultValues: [macadress]
							}}*/
							isRequired={[true]}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyTertiary,
							}}
							listOverrides={{
								listStyle: ListStyle_Types.vertical,
							}}
						/>
					</div>


					<NewButton
						onClick={fetchData}
						disabled={loading}
						content={loading ? "Loading..." : "Change IP"}
						overrides={{
							baseStyles: { marginTop: "1rem", height: "30px", width: "fit-content" }
						}}
					/>

					{error && <p style={{ color: "#880000" }}>{error}</p>}

					{data && (
						<div>
							<p>{data.code} - {data.message}</p>
						</div>
					)}
				</frames.Graph_QL_header>
			</frames.Graph_QL_sections>
		</div>
	);
}
// #endregion

function GraphQl() {
	const [domain, setDomain] = useState<string | undefined>(undefined)
	const inputRef = useRef<NewInputListHandles>(null)

	const [ConnectionArgs, setConnectionArgs] = useState<{ domain: string, gate: number, device: number }>({ domain: "", gate: 1, device: 40 })
	const { getScales, getWaters, getState, stateData: LiveScales, scalesData: ScalesData, deleteEntries: DeleteAutopig, } = useGetAutopig()

	const client = useApolloClient()
	const [DataState, setDataState] = useState<any[]>([])

	type Farm = {
		name: string;
		domain: string;
	};
	const { userFarms, getUserFarms, doLogOut, userToken } = useUser();

	useEffect(() => {
		getUserFarms();
	}, [getUserFarms]);

	const domains: { label: string; value: string }[] = (userFarms ?? []).map(
		(farm: Farm) => ({
			label: farm.domain,
			value: farm.domain,
		})
	);

	//TODO: Update inside the required useGetXXX to contain fetch handler calls 
	const [DeviceData, setDeviceData] = useState<string[]>([])

	const [activeBtnGroup, setActiveBtnGroup] = useState<"log" | "config" | "software">("log")

	const [viewType, setViewType] = useState<"live" | "internal" | "daily" | "log" | "water" | "temp" | "rfid" | "dailyrfid" | "firmware" | "vmSoftware" | "globalSettings" | "weightLimits" | {/*"resetscale" | "Tarascale"| "calibratescale"*/ } | "changegate" | "changemdi" | "changedomain" | "changeip" | "default">("default")
	const [ViewContentBlock, setViewContentBlock] = useState<any | undefined>(<DefaultViewBlock />)
	const [selectedRfid, setSelectedRfid] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [deviceConfig, setDeviceConfig] = useState<DeviceConfig | null>(null);
	const [gateInput, setGateInput] = useState<number | null>(null);
	const [deviceInput, setDeviceInput] = useState<number | null>(null);

	const macFromConfig = useMemo(() => {
		return deviceConfig?.cpuid
			? extractMacFromCpuid(deviceConfig.cpuid)
			: null;
	}, [deviceConfig]);

	useEffect(() => {
		if (viewType === "live" && LiveScales && LiveScales.length) {
			setViewContentBlock(<LiveViewBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "internal") {
			setViewContentBlock(<InternalViewBlock LiveScales={LiveScales} ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "daily") {
			setViewContentBlock(<DailyViewBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "log") {
			setViewContentBlock(<LogViewBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "water") {
			setViewContentBlock(<WaterViewBlock ConnectionArgs={ConnectionArgs} />)
		} else if (viewType === "temp") {
			setViewContentBlock(<TempViewBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "rfid") {
			setViewContentBlock(<AllRfidViewBlock ConnectionArgs={ConnectionArgs} setViewType={setViewType} setSelectedRfid={setSelectedRfid} />);
		} else if (viewType === "dailyrfid") {
			setViewContentBlock(<DailyScalesByRFIDViewBlock ConnectionArgs={ConnectionArgs} initialRfid={selectedRfid} />);
		} else if (viewType === "firmware") {
			setViewContentBlock(<ChangeFirmwareBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "vmSoftware") {
			setViewContentBlock(<ChangeVmSoftwareBlock ConnectionArgs={ConnectionArgs} CallArgs={{
				filename: "Autopig"
			}} />);
		} else if (viewType === "globalSettings") {
			setViewContentBlock(<ChangeGlobalSettingsBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "weightLimits") {
			setViewContentBlock(<SetWeightLimitsBlock ConnectionArgs={ConnectionArgs} />);
		} else if (viewType === "changemdi") {
			setViewContentBlock(<ChangeMdiBlock key={isConnected ? "connected" : "disconnected"} ConnectionArgs={ConnectionArgs} macFromConfig={macFromConfig} />);
		} else if (viewType === "changegate") {
			setViewContentBlock(<ChangeGateBlock key={isConnected ? "connected" : "disconnected"} ConnectionArgs={ConnectionArgs} macFromConfig={macFromConfig} />);
		} else if (viewType === "changedomain") {
			setViewContentBlock(<ChangeDomainBlock key={isConnected ? "connected" : "disconnected"} ConnectionArgs={ConnectionArgs} macFromConfig={macFromConfig} />);
		} else if (viewType === "changeip") {
			setViewContentBlock(<ChangeIpBlock key={isConnected ? "connected" : "disconnected"} ConnectionArgs={ConnectionArgs} macFromConfig={macFromConfig} />);
		} else {
			setViewContentBlock(<DefaultViewBlock />);
		}
	}, [LiveScales, ScalesData, viewType, ConnectionArgs, isConnected, macFromConfig]);

	const ClickHandler = (BtnEvent: LogBtns | ConfigurationBtns | SoftwareBtns) => {
		DeleteAutopig();

		if (domain && inputRef.current) {
			const gate: string | number | undefined = inputRef.current?.value(0) as string | undefined;
			const device: string | number | undefined = inputRef.current?.value(1) as string | undefined;

			if (domain && gate && device) {
				const parsedGate = Number.parseInt(gate);
				const parsedDevice = Number.parseInt(device);

				setConnectionArgs({
					domain: domain,
					gate: parsedGate, device: parsedDevice
				})

				localStorage.setItem("activedomain", domain)

				switch (BtnEvent) {
					case LogBtns.InternalScales:
						getState(Graph_QL.create({ domain, gate: parsedGate, device: parsedDevice }));
						setViewType("internal");
						break
					case LogBtns.LiveScales:

						getState(Graph_QL.create({ domain, gate: parsedGate, device: parsedDevice }));
						setViewType("live");
						break;
					case LogBtns.DailyScales:
						setViewType("daily");

						break;
					case LogBtns.Waters:
						setViewType("water");
						break;
					case LogBtns.Temps:
						setViewType("temp");
						break;

					case LogBtns.GetLog:
						setViewType("log");
						break;

					case LogBtns.AllRfids:
						setViewType("rfid");
						break;
					case LogBtns.DailyScalesByRFID:
						setViewType("dailyrfid");
						break;
					case SoftwareBtns.Firmware:
						setViewType("firmware")
						break;
					case SoftwareBtns.VmSoftware:
						setViewType("vmSoftware");
						break;
					case SoftwareBtns.GlobalSettings:
						setViewType("globalSettings");
						break;
					case SoftwareBtns.WeightLimits:
						setViewType("weightLimits");
						break;
					case ConfigurationBtns.ChangeMdi:
						setViewType("changemdi");
						break;
					case ConfigurationBtns.ChangeGate:
						setViewType("changegate");
						break;
					case ConfigurationBtns.ChangeDomain:
						setViewType("changedomain");
						break;
					case ConfigurationBtns.ChangeIp:
						setViewType("changeip");
						break;
					default:
						break;
				}
			}
		}
	};

	// #endregion
	// #region | Device Connection
	const mobileXs = useMediaQuery(`(max-width: 640px)`);
	const hideViewMenu = useMediaQuery(`(max-width: 914px), (min-width: 1000px) and (max-width: 1233px)`);
	const stackViewMenu = useMediaQuery(`(max-width: 740px), (min-width: 1000px) and (max-width: 1060px)`);
	const stackFields = useMediaQuery(`(max-width: 770px), (min-width: 999px) and (max-width: 1090px)`);
	//const hideLogButtons = useMediaQuery(`(max-width: 633px)`);
	const hideLogButtons = useMediaQuery(`(max-width: 946px), (min-width: 1000px) and (max-width: 1266px)`);
	const vertical = useMediaQuery(`(max-width: 740px), (min-width: 1000px) and (max-width: 1060px)`);
	const heightMax = useMediaQuery(`(max-height: 785px)`);

	const [fetchStartConfigs, { data, loading, error }] = useLazyQuery<
		StartConfigsResponse,
		GetStartConfigsArgs
	>(GetStartConfigs, {
		context: { clientName: "legacy" },
		fetchPolicy: "network-only",
	});

	type DeviceConfig = { cpuid: string };

	const [connectedArgs, setConnectedArgs] = useState<{ domain: string; gate: number; device: number } | null>(null);

	const [copyBtn, setCopyBtn] = useState("Copy MAC");

	useEffect(() => {
		if (data?.getStartConfigs?.length) {
			try {
				const parsed = JSON.parse(data.getStartConfigs[0].value);
				setDeviceConfig(parsed);
				setIsConnected(true);
			} catch (e) {
				console.error("Failed to parse config", e);
			}
		}
	}, [data]);

	const handleInputChange = () => {
		const gate = Number(inputRef.current?.value(0) ?? 0);
		const device = Number(inputRef.current?.value(1) ?? 0);
		setGateInput(gate);
		setDeviceInput(device);
	};

	useEffect(() => {
		const interval = setInterval(handleInputChange, 200);
		return () => clearInterval(interval);
	}, []);

	// Track input changes
	useEffect(() => {
		const currentDomain = localStorage.getItem("activedomain") ?? "";
		const gate = gateInput ?? 0;
		const device = deviceInput ?? 0;

		if (
			currentDomain !== ConnectionArgs.domain ||
			gate !== ConnectionArgs.gate ||
			device !== ConnectionArgs.device
		) {
			setViewType("default");
		}
		if (
			isConnected &&
			connectedArgs &&
			(currentDomain !== connectedArgs.domain ||
				gate !== connectedArgs.gate ||
				device !== connectedArgs.device)
		) {
			setDeviceConfig(null);
			setIsConnected(false);
			setConnectedArgs(null);
		}
	}, [domain, gateInput, deviceInput, ConnectionArgs, isConnected, connectedArgs]);



	function extractMacFromCpuid(cpuid: string): string {
		const parts = cpuid.split("-");
		return parts.slice(-3).join("-");
	}
	const deviceConnection = (
		<div
			style={{
				display: "flex",
				gap: "10px",
				flexWrap: "wrap",
				justifyContent: "flex-start",
				alignItems: stackFields ? "stretch" : "center",
			}}
		>
			<NewCreatableSelect
				placeholder="Domain"
				fieldOverrides={{
					fieldStyle: NewFieldStyles.greyDarkPrimary,
				}}
				options={domains || ""}
				isClearable
				isSearchable
				onChange={(e) => {
					const domain = e?.label ?? "";
					localStorage.setItem("activedomain", domain);
					setDomain(domain);
				}}
			/>

			<NewInputList
				ref={inputRef}
				placeholders={["GateWay", "Device ID"]}
				isRequired={[false, false]}
				fieldOverrides={{
					fieldStyle: NewFieldStyles.greyDarkPrimary,
					fieldWidth: "137.5px",
				}}
				valueOverrides={{
					valueTypes: ["number", "number"],
				}}
				listOverrides={{
					listStyle: ListStyle_Types.horizontal,
				}}
			/>
			<NewButton
				onClick={() => {
					const gate = Number(inputRef.current?.value(0) ?? 0);
					const device = Number(inputRef.current?.value(1) ?? 0);
					const currentDomain = localStorage.getItem("activedomain") || "";

					if (!isConnected) {
						if (!gate || !device || !currentDomain) {
							return alert("Enter valid Domain, Gateway, and Device ID");
						}
						fetchStartConfigs({
							variables: { gatewayindex: gate, mapdeviceindex: device, domain: currentDomain },
						});
						setConnectedArgs({ domain: currentDomain, gate, device });
					} else {
						setDeviceConfig(null);
						setIsConnected(false);
						setConnectedArgs(null);
						setViewType("default");
					}
				}}
				content={!isConnected ? "Connect" : "Disconnect"}
				overrides={{ baseStyles: { height: "30px", width: "fit-content" } }}
			/>

			{loading && <div>Loading...</div>}
			{error && <div style={{ color: "#ffaaaa" }}>Error: {error.message}</div>}
			{deviceConfig && (
				<div
					style={{
						...fonts.fontSize4,
						color: colors.greyLightTertiary,
						...fonts.header,
					}}
				>
					CPU ID: {deviceConfig.cpuid}
					<NewButton
						content={copyBtn}
						onClick={() => {
							navigator.clipboard.writeText(extractMacFromCpuid(deviceConfig.cpuid))
								.then(() => {
									setCopyBtn("Copied");
									setTimeout(() => setCopyBtn("Copy MAC"), 5000);
								})
								.catch(() => setCopyBtn("Failed"));
						}}
						overrides={{
							baseStyles: { height: "1.5rem", width: "fit-content", margin: "5px" }
						}}
					/>
				</div>
			)}
		</div>
	);

	// #endregion
	// #region | Toolbar Structure
	return (
		// outer, grid: knapper øverst, hvidt felt nederst 
		<div style={{
			display: "grid",
			gridTemplateColumns: hideViewMenu ? "1fr 70px" : "1fr 213px",
			gridTemplateRows: stackFields ? "120px 1fr 35px" : "70px 1fr",
			height: "100%",
			width: "100%",
			boxSizing: "border-box",
		}}>

			{/* Device Connection area */}
			<div style={{
				gridArea: "1/1/2/2", // outer grid area = knapper øverst
				display: "flex",
				padding: "0px 0px 0px 0px",
				alignItems: "center",
			}}>
				{deviceConnection}
			</div>

			{/* btn area */}
			<div style={{
				gridArea: "1/2/2/3", // outer grid area = knapper øverst
				display: "flex",
				padding: "0px 10px 0px 0px",
				alignItems: "end",
				justifyContent: "flex-end",
			}}>
				<NewButtonList
					contents={["Log", "Config", "Software"]}
					buttonType={ButtonTypes.tableButton}
					startEnhancers={[
						glyphs.Animal({}),
						glyphs.Animal({}),
						glyphs.Animal({})
					]}
					onClick={(i => {
						if (i === 0) { setActiveBtnGroup("log"); setViewType("log"); }
						if (i === 1) { setActiveBtnGroup("config"); setViewType("changegate"); }
						if (i === 2) { setActiveBtnGroup("software"); setViewType("firmware"); }
					})}
					popOverrides={{
						hasPop: hideViewMenu ? true : false,
						popoverStyle: { backgroundColor: colors.greyDarkSecondary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", left: "unset", top: "-10px", right: "55px" },
						buttonType: ButtonTypes.tableButton,
						openContent: "Sections",
						closeContent: "Sections",
						closeEnhancer: <div style={{ marginBottom: "-4px" }}>{glyphs.Close({})}</div>,
						openEnhancer: <>{glyphs.Animal({})}</>
					}}
				/>
			</div>

			{/* View content area */}
			<div style={{ gridArea: "2/1/3/3", /*outer grid area = hvidt felt nederst*/ width: "100%", height: "100%", border: "5px solid " + colors.greyPrimary, borderRadius: "10px", boxSizing: "border-box", overflow: "hidden" }}>


				{/* hvidt felt */}
				<div style={{ display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "35px 1fr", boxSizing: "border-box", height: "100%" }}>

					{/* hvidt felt header area / gråt område med knapper */}
					<div style={{ gridArea: "1/1/2/2", backgroundColor: colors.greyPrimary, display: "flex", alignItems: "center", padding: "0px 20px", borderBottom: "5px solid " + colors.greyPrimary }}>
						<NewButtonList
							contents={activeBtnGroup === "software" ? Object.values(SoftwareBtns) : activeBtnGroup === "log" ? Object.values(LogBtns) : Object.values(ConfigurationBtns)}
							buttonType={ButtonTypes.textButton}

							listOverrides={{
								listStyle: ListStyle_Types.group,
								listGap: "5px"

							}}
							overrides={{
								baseStyles: {
									marginRight: "15px",
								}
							}}
							onClick={(e) => ClickHandler(Object.values(activeBtnGroup === "software" ? SoftwareBtns : activeBtnGroup === "log" ? LogBtns : ConfigurationBtns)[e])}
							popOverrides={{
								hasPop: hideLogButtons ? true : false,
								popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", right: "unset", top: "-10px", left: "55px", marginTop: "10px" },
								//buttonType: ButtonTypes.tableButton,
								openContent: "Tools",
								closeContent: "Tools",
								closeEnhancer: <div style={{ marginBottom: "-4px" }}>{glyphs.Close({})}</div>,
								openEnhancer: <>{glyphs.Animal({})}</>
							}}
						/>
					</div>

					{/* hvidt felt content area / hvidt område */}
					<div style={{ gridArea: "2/1/3/2", backgroundColor: colors.greyLightTertiary }}>
						{ViewContentBlock ? ViewContentBlock : <></>}
					</div>

				</div>

			</div>

		</div> // outer done
	);
}
